/**
 * AddFamilyHistoryEditor — inline Add/Edit form for the Family medical
 * history section of the History chart tab (PROD-827 chart write
 * surfaces).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/history_tab.dart`
 * `editFamilyHistoryView()` at L714-1244.
 *
 * **Multi-diagnosis pattern**: one row can carry MULTIPLE diagnoses
 * (`code[]`). Each diagnosis has its own from/to date range, problem
 * code (ICD-10), free-text description, and (on Edit) its own recorder
 * + speciality. The "Add Problem" / "Add Another Problem" button
 * appends a row; the delete icon next to each row removes it. Matches
 * Flutter L1086-L1124.
 *
 * **Two modes, same form:**
 * - Add (`editing` omitted) → POST `/CreateFamilyMedicalHistory` with
 *   `CreateFamilyMedicalHistoryBody`. Every diagnosis row gets
 *   `updatedby: currentUserId`, `updatedDate: null`,
 *   `performerDetails: null`, `speciality: null` — server fills the
 *   per-row recorder on insert.
 * - Edit (`editing` is a `PatientFamilyHistory`) → PUT
 *   `/familyMedicalHistory/{id}` with `UpdateFamilyMedicalHistoryBody`.
 *   - Existing rows preserve their original `performerDetails`,
 *     `speciality`, `updatedby`, `updatedDate` (round-trip).
 *   - Newly added rows on Edit get stamped with the editing user.
 *   - `createdBy`, `createdDate`, `encounter` preserved from the
 *     original record. Strict guard refuses to ship if `createdBy` is
 *     missing.
 *
 * **Family-History-specific wire quirks** (curl-verified 2026-05-20):
 * - Top-level literals: `dataAbsentReason: "n/a"`, `fullName: "n/a"`,
 *   `outCome: "n/a"`, `note: ""`. Match Flutter `toJson` defaults.
 * - `outCome` is the typo wire literal (NOT `outcome`).
 * - `gender: null`, `age: 0`, `dob: null` (no UI to change these in
 *   the chart-tab editor — they're set on emergency-contact flow only).
 * - `relationship.system: "NAB041"` — FamilyHistory taxonomy.
 */

import { useMemo, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button, FieldLabel, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useRelationships } from '@/services/common/use-relationships';
import { useDiagnosisCodeSearch } from '@/patients/services/use-diagnosis-code-search';
import {
  readStringField,
  unwrapMasterRow,
} from '@/patients/lib/unwrap-master-row';
import { useCreateFamilyMedicalHistory } from '@/patients/services/use-create-family-medical-history';
import { useUpdateFamilyMedicalHistory } from '@/patients/services/use-update-family-medical-history';
import { normalizePerformerDetails } from './add-allergy-dialog';
import { DynamicDateInput } from './dynamic-date-input';
import type {
  CreateFamilyMedicalHistoryBody,
  FamilyHistoryCodeElement,
  PatientFamilyHistory,
  UpdateFamilyMedicalHistoryBody,
} from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';

// ──────────────────────────────────────────────────────────────────────────
// Zod schema
// ──────────────────────────────────────────────────────────────────────────

/** One diagnosis row in the form. `_carry` holds the round-trip fields
 *  from an existing record (performerDetails, speciality, updatedby,
 *  updatedDate) — opaque to the form, stripped on submit. */
const codeRowSchema = z
  .object({
    diagnosisJson: z.string().min(1, 'Pick a problem'),
    description: z.string().max(100).optional(),
    fromDisplay: z.string().min(1, 'From date is required').max(50),
    fromIso: z.string().nullable(),
    toDisplay: z.string().max(50).optional(),
    toIso: z.string().nullable().optional(),
    /** JSON-encoded original-row carry-over fields. Empty string on
     *  newly-added rows. */
    carryJson: z.string(),
  })
  .refine((v) => v.fromIso !== null, {
    message:
      'From date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
    path: ['fromDisplay'],
  })
  .refine(
    (v) => {
      if (!v.toDisplay?.trim()) return true;
      return v.toIso != null;
    },
    {
      message:
        'To date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
      path: ['toDisplay'],
    },
  );

const schema = z.object({
  relationshipJson: z.string().min(1, 'Pick a relationship'),
  codes: z.array(codeRowSchema).min(1, 'Add at least one problem'),
});

type FormValues = z.infer<typeof schema>;
type CodeRowValues = FormValues['codes'][number];

/**
 * Stored on each row's `carryJson` — the round-trip fields that the
 * Update wire body must preserve verbatim from the original record.
 * Empty (`{}`) on newly-added rows; populated from the existing
 * `editing.code[]` row in `initialValues`.
 *
 * **Why JSON-stringified, not a nested form object?**
 * React Hook Form's `useFieldArray` works on scalar / string fields
 * cleanly; nested objects with optional Coding shapes inside a field-
 * array row hit edge cases with controller-rerenders during row
 * removal. JSON-stringifying the opaque round-trip blob keeps RHF's
 * row mutation primitives (`append`, `remove`, swap) deterministic
 * while preserving the typed shape via `safeParse<RowCarry>`.
 *
 * **Type contract** (this interface) IS the schema — every field is
 * an exact match to the corresponding `FamilyHistoryCodeElement` wire
 * field. If the wire shape drifts, TS catches the mismatch at the
 * `safeParse<RowCarry>(row.carryJson)` call site below (and the
 * normalisation falls back to `null` for any missing field). The
 * `performerDetails` field aliases the wire type's full shape via
 * `FamilyHistoryCodeElement['performerDetails']` — single source of
 * truth, no drift.
 */
interface RowCarry {
  isDeleted?: boolean;
  updatedby?: string | null;
  updatedDate?: string | null;
  performerDetails?: FamilyHistoryCodeElement['performerDetails'];
  speciality?: CodeWordCase | null;
}

/** Picker row used inside `diagnosisJson`. Mirrors `ConditionCode` from
 *  the existing Problems Add flow but trimmed to what we need on the
 *  wire (`code` / `display` / `system` / `description`). */
interface DiagnosisPick {
  code: string | null;
  display: string | null;
  system: string | null;
  description: string | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Editor
// ──────────────────────────────────────────────────────────────────────────

export interface AddFamilyHistoryEditorProps {
  patientId: string;
  onClose: () => void;
  editing?: PatientFamilyHistory | null;
}

export function AddFamilyHistoryEditor({
  patientId,
  onClose,
  editing = null,
}: AddFamilyHistoryEditorProps) {
  const isEditing = !!editing?.identifier;

  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  const initialValues = useMemo<FormValues>(() => {
    if (!editing) {
      return {
        relationshipJson: '',
        codes: [emptyCodeRow()],
      };
    }
    const relRow = editing.relationship
      ? {
          code: editing.relationship.code ?? null,
          display: editing.relationship.display ?? null,
          system: editing.relationship.system ?? null,
        }
      : null;
    const existingCodes = (editing.code ?? [])
      .filter((c) => c.isDeleted !== true)
      .map<CodeRowValues>((c) => {
        const pick: DiagnosisPick = {
          code: c.code ?? null,
          display: c.display ?? null,
          system: c.system ?? null,
          description: c.description ?? null,
        };
        const carry: RowCarry = {
          isDeleted: false,
          updatedby: c.updatedby ?? null,
          updatedDate: c.updatedDate ?? null,
          performerDetails: normalizePerformerDetails(c.performerDetails),
          speciality: c.speciality ?? null,
        };
        return {
          diagnosisJson: JSON.stringify(pick),
          description: c.description ?? '',
          fromDisplay: c.fromDisplayTime ?? '',
          fromIso: c.fromTime ?? null,
          toDisplay: c.toDisplayTime ?? '',
          toIso: c.toTime ?? null,
          carryJson: JSON.stringify(carry),
        };
      });
    return {
      relationshipJson: relRow ? JSON.stringify(relRow) : '',
      codes: existingCodes.length > 0 ? existingCodes : [emptyCodeRow()],
    };
  }, [editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const codes = useFieldArray({
    control: form.control,
    name: 'codes',
  });

  const createMut = useCreateFamilyMedicalHistory();
  const updateMut = useUpdateFamilyMedicalHistory();
  const isPending = createMut.isPending || updateMut.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }
    const rel = safeParse<{
      code: string | null;
      display: string | null;
      system: string | null;
    }>(values.relationshipJson);
    if (!rel) {
      toast.error(
        'Form values are corrupted — please re-pick the relationship.',
      );
      return;
    }

    // Map each form row to a FamilyHistoryCodeElement. Existing rows
    // (carryJson populated) round-trip their original
    // performerDetails+speciality; newly added rows get a fresh
    // updatedby stamp.
    const wireCodes: FamilyHistoryCodeElement[] = [];
    for (const row of values.codes) {
      const pick = safeParse<DiagnosisPick>(row.diagnosisJson);
      if (!pick) {
        toast.error(
          'A problem entry is corrupted — please re-pick the diagnosis.',
        );
        return;
      }
      const carry = safeParse<RowCarry>(row.carryJson) ?? {};
      const isExistingRow = !!carry.updatedby || !!carry.performerDetails;
      wireCodes.push({
        system: pick.system ?? null,
        code: pick.code ?? null,
        display: pick.display ?? null,
        description: row.description?.trim() || null,
        fromTime: row.fromIso,
        fromDisplayTime: row.fromDisplay,
        toTime: row.toIso ?? null,
        toDisplayTime: row.toDisplay?.trim() ? row.toDisplay : null,
        isDeleted: false,
        // Existing rows preserve their original updatedby (audit trail).
        // Newly added rows on an Edit get stamped with the editing user.
        updatedby: isExistingRow ? (carry.updatedby ?? userId) : userId,
        updatedDate: carry.updatedDate ?? null,
        // Carry the per-row recorder + speciality verbatim from the
        // original record (populated in initialValues via
        // `normalizePerformerDetails`); new rows ship null and the
        // server fills the per-row recorder on insert.
        performerDetails: carry.performerDetails ?? null,
        speciality: carry.speciality ?? null,
      });
    }

    const relationshipCoding: CodeWordCase = {
      code: rel.code ?? null,
      display: rel.display ?? null,
      system: rel.system ?? null,
      description: null,
      shortName: null,
    };

    try {
      if (isEditing && editing) {
        // Strict audit guard — same UX-visible failure mode as
        // `add-social-history-editor.tsx` (Samer PR #245 review §5):
        // a missing original `createdBy` surfaces a sonner toast and
        // returns; editor stays open with user input intact. See the
        // `EditModeMissingRecorder` Storybook story for the visible
        // path.
        if (!editing.createdBy) {
          toast.error(
            'Cannot edit — this record is missing its original recorder. Please contact support.',
          );
          return;
        }
        const body: UpdateFamilyMedicalHistoryBody = {
          identifier: editing.identifier!,
          code: wireCodes,
          status: 'Active',
          patient: patientId,
          dataAbsentReason: 'n/a',
          fullName: 'n/a',
          relationship: relationshipCoding,
          gender: null,
          age: 0,
          dob: null,
          outCome: 'n/a',
          note: editing.note ?? '',
          statusReason: null,
          encounter: editing.encounter ?? null,
          isDeleted: false,
          createdDate: editing.createdDate ?? null,
          createdBy: editing.createdBy,
        };
        await updateMut.mutateAsync({
          patientId,
          familyHistoryId: editing.identifier!,
          body,
        });
        toast.success(
          `Updated family history: ${rel.display ?? rel.code ?? 'Unknown'}`,
        );
      } else {
        const body: CreateFamilyMedicalHistoryBody = {
          identifier: null,
          code: wireCodes,
          status: 'Active',
          patient: patientId,
          dataAbsentReason: 'n/a',
          fullName: 'n/a',
          relationship: relationshipCoding,
          gender: null,
          age: 0,
          dob: null,
          outCome: 'n/a',
          note: '',
          statusReason: null,
          encounter: null,
          isDeleted: false,
          createdDate: null,
          createdBy: userId,
        };
        await createMut.mutateAsync({ patientId, body });
        toast.success(
          `Added family history: ${rel.display ?? rel.code ?? 'Unknown'}`,
        );
      }
      form.reset();
      onClose();
    } catch {
      /* Global toast surfaces failure; keep editor open. */
    }
  });

  return (
    // The design's PaEditorCard: the one card (hairline, 12 corners, white, no shadow, 24 padding); look in
    // showcase-patient.css `editor card`.
    <div data-slot="editor-card">
      <form onSubmit={submit} className="space-y-6">
        <Controller
          name="relationshipJson"
          control={form.control}
          render={({ field, fieldState }) => (
            <RelationshipPicker
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <div className="space-y-4">
          {codes.fields.map((rowField, idx) => (
            // `rowField.id` is the stable RHF-generated row key — survives
            // sibling removal. Controllers in <FamilyHistoryDiagnosisRow> reference
            // `codes.${index}.fieldName` paths; RHF's `useFieldArray.remove`
            // shifts those paths atomically (no manual re-indexing, no
            // dropped state). Each surviving row's From/To dates,
            // diagnosis pick, description, and carryJson stay intact —
            // RHF's internal field map is keyed by the stable row id, and
            // `remove(idx)` rewrites the index → id map in one tick.
            // Verified by manually toggling row 2 of a 3-row edit and
            // submitting: rows 1 + 3 round-trip with their full
            // performerDetails / dates / descriptions intact.
            <FamilyHistoryDiagnosisRow
              key={rowField.id}
              index={idx}
              form={form}
              onRemove={
                codes.fields.length > 1 ? () => codes.remove(idx) : undefined
              }
            />
          ))}
        </div>

        <button
          type="button"
          className="text-sm font-semibold text-brand-primary hover:text-brand-depth"
          onClick={() => codes.append(emptyCodeRow())}
        >
          {codes.fields.length > 0 ? 'Add Another Problem' : 'Add Problem'}
        </button>

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                Saving…
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Done'
            )}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Relationship picker — `/api/Master/Relation?category=FamilyHistory`
// ──────────────────────────────────────────────────────────────────────────

function RelationshipPicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const relQ = useRelationships({
    query: debounced.trim(),
    category: 'FamilyHistory',
  });

  const selected = useMemo<{
    code: string | null;
    display: string | null;
    system: string | null;
  } | null>(() => {
    if (!value) return null;
    return safeParse<{
      code: string | null;
      display: string | null;
      system: string | null;
    }>(value);
  }, [value]);

  const results = relQ.data ?? [];

  return (
    <div>
      <FieldLabel
        htmlFor="relationship"
        
      >
        Relationship <span className="text-red-700">*</span>
      </FieldLabel>

      {selected ? (
        <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
          <p className={v2Look('text-sm font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
            {selected.display ?? selected.code ?? '—'}
          </p>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            onClick={() => {
              onChange('');
              setQuery('');
            }}
          >
            Change
          </Button>
        </div>
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              id="relationship"
              type="text"
              placeholder="Type to search (e.g. mother, father, sibling)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          <div className="mt-1 max-h-56 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
            {relQ.isFetching ? (
              <BrandLoading size={32} label="Loading…" />
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-sm text-text-muted">
                {debounced.trim() ? 'No matches. Try another word or a code.' : 'No relationships available.'}
              </div>
            ) : (
              results.map((row, idx) => {
                const flat = {
                  code: row.code ?? null,
                  display: row.name ?? null,
                  system: row.system ?? null,
                };
                const rowKey = `${flat.code ?? ''}-${flat.display ?? ''}-${idx}`;
                return (
                  <button
                    key={rowKey}
                    type="button"
                    onClick={() => {
                      onChange(JSON.stringify(flat));
                      setQuery('');
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                  >
                    <span className="text-sm text-text-primary">
                      {flat.display ?? flat.code ?? '—'}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Per-diagnosis row
// ──────────────────────────────────────────────────────────────────────────

function FamilyHistoryDiagnosisRow({
  index,
  form,
  onRemove,
}: {
  index: number;
  form: UseFormReturn<FormValues>;
  onRemove?: () => void;
}) {
  return (
    <div className="relative rounded-md border border-border-default p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name={`codes.${index}.diagnosisJson` as const}
          control={form.control}
          render={({ field, fieldState }) => (
            <DiagnosisPicker
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <div>
          <FieldLabel
            htmlFor={`description-${index}`}
            
          >
            Problem description
          </FieldLabel>
          <TextField
            id={`description-${index}`}
            {...form.register(`codes.${index}.description` as const)}
            placeholder="Enter description"
            maxLength={100}
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name={`codes.${index}.fromIso` as const}
          control={form.control}
          render={({ field: isoField }) => (
            <Controller
              name={`codes.${index}.fromDisplay` as const}
              control={form.control}
              render={({ field: displayField, fieldState }) => (
                <DynamicDateInput
                  label="From"
                  required
                  // A relative's onset is usually decades back — year dropdown.
                  captionLayout="dropdown"
                  displayValue={displayField.value}
                  isoValue={isoField.value}
                  onChange={(next) => {
                    displayField.onChange(next.displayValue);
                    isoField.onChange(next.isoValue);
                  }}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
        />
        <Controller
          name={`codes.${index}.toIso` as const}
          control={form.control}
          render={({ field: isoField }) => (
            <Controller
              name={`codes.${index}.toDisplay` as const}
              control={form.control}
              render={({ field: displayField, fieldState }) => (
                <DynamicDateInput
                  label="To"
                  captionLayout="dropdown"
                  displayValue={displayField.value ?? ''}
                  isoValue={isoField.value ?? null}
                  onChange={(next) => {
                    displayField.onChange(next.displayValue);
                    isoField.onChange(next.isoValue);
                  }}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
        />
      </div>

      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove problem ${index + 1}`}
          className="icon-action absolute top-3 right-3"
        >
          <Trash2 className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ICD-10 diagnosis picker (reused pattern from Problems' editor)
// ──────────────────────────────────────────────────────────────────────────

function DiagnosisPicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const searchQ = useDiagnosisCodeSearch(debounced, true);

  const selected = useMemo<DiagnosisPick | null>(
    () => (value ? safeParse<DiagnosisPick>(value) : null),
    [value],
  );

  const results = searchQ.data ?? [];

  return (
    <div>
      <FieldLabel
        
      >
        Problem name or code <span className="text-red-700">*</span>
      </FieldLabel>

      {selected ? (
        (() => {
          // Defensive re-unwrap in case a legacy stored value still
          // carries the wrapped master-row shape from a save BEFORE
          // this fix landed.
          const inner = unwrapMasterRow(selected);
          const codeStr = readStringField(inner, 'code') ?? '';
          const displayStr = (readStringField(inner, 'display') ?? '').trim();
          return (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
              <div className="min-w-0">
                <p className={v2Look('text-sm font-medium text-brand-depth truncate', V2_BODY_EMPHASIS)}>
                  {displayStr || codeStr || '—'}
                </p>
                {codeStr ? (
                  <p className="text-xs text-text-hint">{codeStr}</p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onChange('');
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              type="text"
              placeholder="Type to search (e.g. diabetes, S72.0)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          {debounced.trim().length >= 2 ? (
            <div className="mt-1 max-h-56 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
              {searchQ.isFetching ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  No matches. Try another word or a code.
                </div>
              ) : (
                results.map((row, idx) => {
                  // Defensive unwrap — `DiagnosisCodes` master endpoint
                  // returns rows wrapped `{code: ActualCoding}` (PR #225
                  // browser-fix). Without this, picking a diagnosis on
                  // Family History would crash the same way Problems did
                  // before the fix. See `unwrap-master-row.ts` for full
                  // background.
                  const inner = unwrapMasterRow(row);
                  if (!inner) return null;
                  const flat: DiagnosisPick = {
                    code: readStringField(inner, 'code'),
                    display: readStringField(inner, 'display'),
                    system: readStringField(inner, 'system'),
                    description: null,
                  };
                  const codeStr = flat.code ?? '';
                  const displayStr = (flat.display ?? '').trim();
                  const rowKey = `${codeStr}-${displayStr}-${idx}`;
                  return (
                    <button
                      key={rowKey}
                      type="button"
                      onClick={() => {
                        onChange(JSON.stringify(flat));
                        setQuery('');
                      }}
                      className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                    >
                      <span className="text-sm text-text-primary block">
                        {displayStr || codeStr || '—'}
                      </span>
                      {codeStr ? (
                        <span className="text-xs text-text-hint">
                          {codeStr}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function emptyCodeRow(): CodeRowValues {
  return {
    diagnosisJson: '',
    description: '',
    fromDisplay: '',
    fromIso: null,
    toDisplay: '',
    toIso: null,
    carryJson: '',
  };
}
